import React, { useContext, useState } from 'react';
import z from 'zod';
import {
  Body, Heading, PentestIntakeForm, ErrorMessage,
} from '@stereoagency/ui';
import Turnstile from 'react-turnstile';
import { LinesContext } from '@/components/LinesContext';
import {
  pentestIntakeSchema,
  testingTypeOptions,
  yesNoOptions,
  environmentOptions,
  authUnauthOptions,
  frontendTypeOptions,
  cloudProviderOptions,
} from '@/schemas/pentest-intake';

type Props = {
  data?: {
    label?: string
    title?: string
    description?: string
  } | null
};

type FormType = 'application' | 'red-team';

const schema = pentestIntakeSchema.extend({
  $turnstile_key: process.env.NEXT_PUBLIC_DISABLE_TURNSTILE === 'true'
    ? z.string().optional().default('disabled')
    : z.string().min(1, 'Please complete the captcha'),
});

type SectionConfig = {
  title: string;
  description?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  fields: { name: string; type: string; label: string; placeholder?: string; options?: readonly { readonly label: string; readonly value: string }[] }[][];
};

function buildSections(): SectionConfig[] {
  return [
    {
      title: 'Required Information',
      defaultExpanded: true,
      fields: [
        [{ name: 'testingType', type: 'select', label: 'Type of testing', options: testingTypeOptions }],
        [{ name: 'applicationName', type: 'input', label: 'Name of the application', placeholder: 'e.g. Customer Portal' }],
        [
          { name: 'contactName', type: 'input', label: 'Your name', placeholder: 'John Doe' },
          { name: 'contactEmail', type: 'email', label: 'Your email', placeholder: 'john@example.com' },
        ],
        [{ name: 'contactPhone', type: 'input', label: 'Your phone number', placeholder: '+32 123 45 67 89' }],
      ],
    },
    {
      title: 'General Information',
      defaultExpanded: true,
      fields: [
        [{ name: 'hasAiComponent', type: 'select', label: 'Does the environment use an AI component?', options: yesNoOptions }],
        [{ name: 'applicationDescription', type: 'textarea', label: 'Short description of the application', placeholder: 'Describe the main purpose and functionality...\n\ne.g.\n- Any sensitive utilities? (financial transactions, personal/medical data, etc.)\n- Any specific focus for the penetration test? (e.g. OWASP Top 10, business logic)\n- Does the application have upload features?\n- Does the application have approval flows?\n- Any specific business flows that require extra details? (e.g. payment flow limited to 1000 EUR)\n- Can you provide external access to the resource to be tested?' }],
        [{ name: 'baseUrls', type: 'textarea', label: 'Overall Scope (e.g. IP, URL)', placeholder: 'e.g. https://app.example.com' }],
        [{ name: 'authenticationType', type: 'input', label: 'What type of authentication is being used?', placeholder: 'e.g. OAuth, 2FA, username/password...' }],
        [{ name: 'timing', type: 'input', label: 'When would the testing be executed?', placeholder: 'e.g. Q2 2026, March 2026...' }],
        [{ name: 'roles', type: 'input', label: 'Which user roles should be tested?', placeholder: 'e.g. Anonymous access, authenticated users' }],
      ],
    },
    {
      title: 'Technical Information',
      description: 'Details about front-end, middleware, and backend technologies',
      collapsible: true,
      fields: [
        [{ name: 'frontendType', type: 'select', label: 'Type of front-end', options: frontendTypeOptions }],
        [{ name: 'frontendLanguage', type: 'input', label: 'Front-end programming language', placeholder: 'e.g. JSP, ASP.NET, Java, C#...' }],
        [{ name: 'frontendFrameworks', type: 'input', label: 'Front-end frameworks used', placeholder: 'e.g. Bootstrap, Angular, React, CMS...' }],
        [{ name: 'middlewareType', type: 'input', label: 'Type of middleware', placeholder: 'e.g. MQseries, ESB, Web Service, N/A...' }],
        [{ name: 'middlewareLanguage', type: 'input', label: 'Middleware programming language', placeholder: 'e.g. C#, Java, Cobol...' }],
        [{ name: 'backendType', type: 'input', label: 'Type of backend', placeholder: 'e.g. Mainframe, Database...' }],
        [{ name: 'backendLanguage', type: 'input', label: 'Backend programming language', placeholder: 'e.g. C#, Java, Cobol...' }],
        [{ name: 'externalInterface', type: 'textarea', label: 'Interface with external party', placeholder: 'Describe any external interfaces...' }],
      ],
    },
  ];
}

function buildRedTeamSections(): SectionConfig[] {
  return [
    {
      title: 'Your Contact Details',
      defaultExpanded: true,
      fields: [
        [
          { name: 'contactName', type: 'input', label: 'Your name', placeholder: 'John Doe' },
          { name: 'contactEmail', type: 'email', label: 'Your email', placeholder: 'john@example.com' },
        ],
        [{ name: 'contactPhone', type: 'input', label: 'Your phone number', placeholder: '+32 123 45 67 89' }],
      ],
    },
    {
      title: 'Red Team Exercise Details',
      defaultExpanded: true,
      fields: [
        [{ name: 'redTeamNote', type: 'textarea', label: 'Please describe your red team exercise requirements', placeholder: 'Describe your objectives, scope, timeline, and any other relevant details...' }],
      ],
    },
  ];
}

export default function NvisoPentestIntakeForm({
  data,
}: Props) {
  const { enableLines } = useContext(LinesContext);
  const [success, setSuccess] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formType, setFormType] = useState<FormType>('application');

  const formTypeSelector = (
    <div className="flex gap-1 p-1 bg-fg-default/5 rounded-lg">
      {([
        { value: 'application' as FormType, label: 'Penetration Test' },
        // { value: 'red-team' as FormType, label: 'Red Team' },
      ]).map(({ value, label }) => (
        <div
          key={value}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
            formType === value
              ? 'bg-bg-default shadow-sm text-fg-default'
              : 'text-fg-default/60'
          }`}
        >
          {label}
        </div>
      ))}
    </div>
  );

  const baseSections = formType === 'application' ? buildSections() : buildRedTeamSections();

  const sections = [
    ...baseSections,
    {
      title: 'Verification',
      defaultExpanded: true,
      fields: [
        [{
          name: '$turnstile_key',
          type: 'custom' as const,
          label: 'Captcha verification',
          render: (props: any, errors: any) => {
            if (process.env.NEXT_PUBLIC_DISABLE_TURNSTILE === 'true') return null;
            return (
              <div>
                <Turnstile
                  sitekey={process.env.TURNSTILE_SITE_KEY ?? ''}
                  onVerify={(value: string) => {
                    props.onBlur({ target: { name: '$turnstile_key' } });
                    props.onChange({ target: { name: '$turnstile_key', value } });
                  }}
                />
                {!!errors?.$turnstile_key?.message && (
                  <ErrorMessage className="mt-3">{errors.$turnstile_key.message}</ErrorMessage>
                )}
              </div>
            );
          },
        }],
      ],
    },
  ];

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    try {
      setSubmitting(true);
      const response = await fetch('/api/pentest-intake', {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          debug: process.env.NODE_ENV === 'development',
        }),
        headers: { 'content-type': 'application/json' },
      }).then((r) => r.json()) as {
        success: boolean
        errors?: Record<string, string[]>
      };
      setSuccess(response.success);
    } catch (err) {
      setSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PentestIntakeForm
      key={formType}
      enableLines={enableLines}
      label={data?.label}
      title={data?.title ?? 'Penetration Test Request'}
      description={data?.description}
      headerContent={null /* formTypeSelector */}
      formId="pentest-intake"
      schema={schema}
      sections={sections as any}
      submitContent="Submit Request"
      isSubmitting={submitting}
      errorContent={success === false ? 'Something went wrong. Please try again later.' : null}
      successContent={success ? (
        <div>
          <Heading variant="h3">Thank you!</Heading>
          <Body className="mt-3">Your penetration test request has been submitted successfully. We will get back to you shortly.</Body>
        </div>
      ) : null}
      onSubmit={handleSubmit}
    />
  );
}
